/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { Namespaces } from '../../../i18n';
import { useTableStyles } from '../../../styles/table';
import { EntityId } from '../../../types/api/EntityId';
import { IProjectWithClusters } from '../../../types/project/IProjectWithClusters';
import { AlignCenter } from '../../AlignCenter/AlignCenter';
import { ServiceIcon } from '../../ServiceIcon';
import { createClustersListStyles } from './ProjectClustersList.styles';

type ClustersListProps = {
  clusters: IProjectWithClusters['clusters'];
  onManageClick?: (clusterId: EntityId) => void;
};

export const ProjectManageClustersList = memo<ClustersListProps>(props => {
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const classes = createClustersListStyles();
  const tableClasses = useTableStyles();
  const { clusters, onManageClick } = props;

  const onManageRowClick = onManageClick ?? null;

  return (
    <TableContainer>
      <Table aria-label="clusters table" className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('projects.management.clusters_list.title_cluster')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('projects.management.clusters_list.title_type')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('projects.management.clusters_list.title_status')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {clusters.map(cluster => (
            <TableRow key={cluster.id}>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" className={tableClasses.bodyTableCellText}>
                  {cluster.name}
                </Typography>
              </TableCell>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <ServiceIcon serviceType={cluster.type} variant="minimal" />
              </TableCell>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" className={tableClasses.bodyTableCellText}>
                  {cluster.status}
                </Typography>
              </TableCell>
              {onManageRowClick && (
                <TableCell
                  classes={{
                    root: cx(classes.editColumn, tableClasses.bodyTableCell),
                  }}
                >
                  <div
                    aria-label={t(`${Namespaces.Common}:label.manage_clust`, {
                      id: cluster.id,
                    })}
                    className={classes.editIcon}
                    onClick={() => onManageRowClick(cluster.id)}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
          {!clusters.length && (
            <TableRow>
              <TableCell colSpan={2} classes={{ root: tableClasses.bodyTableCell }}>
                <AlignCenter>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {t('projects.management.clusters_list.no_clusters')}
                  </Typography>
                </AlignCenter>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
